import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Target, Clock, CheckCircle2, MapPin } from "lucide-react";
import BookingDialog from "@/components/BookingDialog";
import { apiClient } from "@/lib/api";
import { COACHES } from "@/constants/site";

const PACKAGES = [
  { type: "one_to_one", title: "1-to-1 Coaching", price: "£35", per: "/ session", features: ["60-minute private session", "Personalised drills", "Position-specific focus"] },
];

export default function PrivateSessions() {
  const [slots, setSlots] = useState([]);
  const [params, setParams] = useSearchParams();
  const initialCoach = params.get("coach") || "all";
  const [selectedCoach, setSelectedCoach] = useState(initialCoach);

  useEffect(() => {
    apiClient.get("/availability").then((r) => setSlots(r.data.slots || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (initialCoach !== selectedCoach) setSelectedCoach(initialCoach);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCoach]);

  const filterCoach = (s) => {
    if (selectedCoach === "all") return true;
    return s.coach.toLowerCase().includes(selectedCoach.toLowerCase());
  };

  const filteredSlots = useMemo(() => slots.filter(filterCoach), [slots, selectedCoach]);

  const setCoach = (slug) => {
    setSelectedCoach(slug);
    const next = new URLSearchParams(params);
    if (slug === "all") next.delete("coach");
    else next.set("coach", slug);
    setParams(next, { replace: true });
  };

  return (
    <div data-testid="private-sessions-page">
      <section className="relative">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/8941569/pexels-photo-8941569.jpeg" className="w-full h-full object-cover opacity-25" alt="One to one coaching" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B1B3A]/85 via-[#1E3A8A]/85 to-[#1E3A8A]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="eyebrow text-[#FBBF24]">Private sessions</div>
          <h1 className="font-display heading-uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mt-3 text-white">
            Built around <span className="text-[#FBBF24]">your player.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-slate-200 text-lg leading-relaxed">
            Focused 1-to-1 sessions tailored to your child's goals — choose the day and the coach that suit you.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid md:grid-cols-2 gap-6 max-w-xl mx-auto">
          {PACKAGES.map((p) => (
            <div key={p.type} data-testid={`package-${p.type}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-8 hover-lift">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-[#1E3A8A] text-white grid place-items-center">
                  <Target className="w-6 h-6" />
                </div>
                <h3 className="font-display uppercase text-2xl">{p.title}</h3>
              </div>
              <div className="flex items-baseline gap-2 mb-6">
                <div className="font-display text-5xl text-[#1E3A8A]">{p.price}</div>
                <div className="text-sm text-slate-500">{p.per}</div>
              </div>
              <ul className="space-y-3 mb-8">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-slate-700 text-sm">
                    <CheckCircle2 className="w-5 h-5 text-[#1E3A8A] flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <BookingDialog
                defaultService={p.type}
                trigger={<button data-testid={`book-${p.type}`} className="btn-gold w-full justify-center">Book This Plan</button>}
              />
            </div>
          ))}
        </div>
      </section>

      <section id="availability" className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="eyebrow text-[#1E3A8A]">Coach availability</div>
          <h2 className="font-display heading-uppercase text-4xl sm:text-5xl mt-3 leading-none">This week's slots.</h2>
          <p className="mt-3 text-slate-500 max-w-2xl">Pick a day that works — we'll confirm the exact venue when you book.</p>

          {/* Coach filter */}
          <div className="mt-8 flex flex-wrap gap-2" data-testid="coach-filter">
            <button
              data-testid="coach-filter-all"
              onClick={() => setCoach("all")}
              className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-widest border-2 transition ${selectedCoach === "all" ? "border-[#1E3A8A] bg-[#1E3A8A] text-white" : "border-slate-300 bg-white text-slate-700 hover:border-[#1E3A8A]/50"}`}
            >
              All coaches
            </button>
            {COACHES.map((c) => (
              <button
                key={c.slug}
                data-testid={`coach-filter-${c.slug}`}
                onClick={() => setCoach(c.slug)}
                className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-widest border-2 transition ${selectedCoach === c.slug ? "border-[#1E3A8A] bg-[#1E3A8A] text-white" : "border-slate-300 bg-white text-slate-700 hover:border-[#1E3A8A]/50"}`}
              >
                {c.name}
              </button>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredSlots.length === 0 ? (
              (() => {
                const coach = COACHES.find((c) => c.slug === selectedCoach);
                return (
                  <div data-testid="coach-no-availability" className="col-span-full rounded-2xl border-2 border-dashed border-slate-300 bg-white p-10 text-center">
                    <div className="eyebrow text-[#1E3A8A]">{coach ? coach.name : "No coach selected"}</div>
                    <h3 className="font-display uppercase text-3xl mt-2">No availability</h3>
                    <p className="mt-3 text-slate-500 max-w-md mx-auto">
                      {coach ? `${coach.name} is fully booked this week.` : "No availability shown."} Message us on WhatsApp to be notified when a slot opens up.
                    </p>
                  </div>
                );
              })()
            ) : filteredSlots.map((s, i) => {
              // Compute next occurrence of this weekday (Mon=1..Thu=4)
              const dayIdx = { Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4 }[s.day];
              const today = new Date(); today.setHours(0, 0, 0, 0);
              const diff = ((dayIdx - today.getDay()) + 7) % 7 || 7;
              const nextDate = new Date(today); nextDate.setDate(today.getDate() + diff);
              return (
                <div key={`${s.day}-${s.coach}-${i}`} data-testid={`availability-${s.day}-${s.coach.replace(/\s+/g, '-').toLowerCase()}`} className="rounded-2xl border border-slate-200 bg-white p-6 hover-lift">
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-display uppercase text-xl">{s.day}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#1E3A8A] font-bold">{s.coach}</div>
                  </div>
                  {s.venue && (
                    <div className="flex items-start gap-1.5 mb-3 text-xs text-slate-600">
                      <MapPin className="w-3.5 h-3.5 text-[#1E3A8A] flex-shrink-0 mt-0.5" />
                      <span>{s.venue}</span>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {s.times.length === 0 ? (
                      <span data-testid={`no-avail-${s.day.toLowerCase()}-${s.coach.replace(/\s+/g, '-').toLowerCase()}`} className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-500 border border-slate-200">
                        No availability
                      </span>
                    ) : s.times.map((t) => (
                      <BookingDialog
                        key={t}
                        defaultService="one_to_one"
                        defaultDate={nextDate}
                        defaultTime={t}
                        coachName={s.coach}
                        trigger={
                          <button
                            type="button"
                            data-testid={`slot-${s.day.toLowerCase()}-${s.coach.replace(/\s+/g, '-').toLowerCase()}-${t}`}
                            className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-[#1E3A8A]/10 text-[#1E3A8A] border border-[#1E3A8A]/30 hover:bg-[#1E3A8A] hover:text-white hover:border-[#1E3A8A] transition cursor-pointer"
                          >
                            <Clock className="w-3 h-3" /> {t}
                          </button>
                        }
                      />
                    ))}
                  </div>
                  {s.times.length > 0 && (
                    <p className="mt-3 text-[10px] uppercase tracking-widest text-slate-400">Tap a time to book</p>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-12 flex justify-center">
            <BookingDialog
              defaultService="one_to_one"
              trigger={<button data-testid="availability-book-btn" className="btn-gold">Book a Slot</button>}
            />
          </div>
        </div>
      </section>
    </div>
  );
}
